import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { ADD_TO_CART_TEXT_ANIMATION_DURATION } from '@theme/product-form';

class CartModalComponent extends DialogComponent {
  // Constants
  static MIN_QUANTITY = 1;
  static MAX_QUANTITY_SELECT = 10;
  static QUANTITY_SWITCHER_TRIGGER = '11';

  #isLoading = false;
  #lastAddedProductId = /** @type {string | null} */ (null);
  #addedProduct = /** @type {AddedProduct | null} */ (null);
  #previousCartState = /** @type {CartState | null} */ (null);
  #attachedQuantitySwitchers = new WeakSet();
  #tabsInitialized = false;

  /** @type {HTMLDialogElement | null} */
  dialogEl = null;
  /** @type {HTMLElement | null} */
  loadingStateEl = null;
  /** @type {HTMLElement | null} */
  modalBodyEl = null;
  /** @type {HTMLElement | null} */
  addedProductContainer = null;
  /** @type {HTMLElement | null} */
  upsellSection = null;
  /** @type {HTMLElement | null} */
  upsellProductsContainer = null;
  /** @type {HTMLElement | null} */
  liveRegion = null;

  connectedCallback() {
    super.connectedCallback();

    this.dialogEl = /** @type {HTMLDialogElement | null} */ (
      this.#getRef('dialog', HTMLDialogElement)
    );
    this.loadingStateEl = /** @type {HTMLElement | null} */ (
      this.#getRef('loadingState', HTMLElement)
    );
    this.modalBodyEl = /** @type {HTMLElement | null} */ (
      this.#getRef('modalBody', HTMLElement)
    );
    this.addedProductContainer = /** @type {HTMLElement | null} */ (
      this.#getRef('addedProductContainer', HTMLElement)
    );
    this.upsellSection = /** @type {HTMLElement | null} */ (
      this.#getRef('upsellSection', HTMLElement)
    );
    this.upsellProductsContainer = /** @type {HTMLElement | null} */ (
      this.#getRef('upsellProductsContainer', HTMLElement)
    );
    this.liveRegion = /** @type {HTMLElement | null} */ (
      this.#getRef('liveRegion', HTMLElement)
    );

    if (this.#isSafari()) {
      document.body.classList.add('is-safari');
    }

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError);

    this.#showLoadingState();
    this.#hideUpsellSection();
    this.#initializeUpsellTabs();
    this.#refreshUpsellCarousels();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartError, this.#handleCartError);
  }

  /**
   * @template {Element} T
   * @param {string} refName
   * @param {new (...args: any[]) => T} ctor
   * @returns {T | null}
   */
  #getRef(refName, ctor) {
    const stored = this.refs?.[refName];

    if (stored instanceof ctor) return /** @type {T} */ (stored);

    if (Array.isArray(stored)) {
      const match = stored.find((entry) => entry instanceof ctor);
      if (match) {
        return /** @type {T} */ (match);
      }
    }

    const queried = this.querySelector(`[ref="${refName}"]`);
    return queried instanceof ctor ? /** @type {T} */ (queried) : null;
  }

  /**
   * Checks if the current browser is Safari
   * @returns {boolean}
   */
  #isSafari() {
    if (typeof navigator === 'undefined') return false;
    
    const userAgent = navigator.userAgent;
    // Check for Safari but exclude Chrome and Chromium-based browsers
    return /Safari/.test(userAgent) && !/Chrome/.test(userAgent) && !/Chromium/.test(userAgent);
  }

  #handleCartAdd = async (/** @type {CartAddEvent} */ event) => {
  // ---- DISABLE CART MODAL ON MOBILE ----
  if (window.matchMedia('(max-width: 350px)').matches) {
    return; // mobile: do nothing
  }
  // --------------------------------------

    const eventData = event.detail?.data || {};

    if (eventData.didError) {
      this.#handleCartError();
      return;
    }

    const productId = this.#extractProductIdentifier(eventData);
    const variantId = eventData.variantId ? String(eventData.variantId) : null;

    if (!productId) return;

    this.#lastAddedProductId = productId;
    this.#showLoadingState();

    if (this.dialogEl && !this.dialogEl.open) {
      this.showDialog();
    }

    this.#updateHeaderText('success');
    this.#announce(this.#getTranslation('content.product_added_to_cart', 'The product is added to cart'));

    // Extract product-title from form input (for translated product titles)
    const productTitle = this.#getProductTitleFromForm(productId);

    try {
      const delay = eventData.source === 'sticky-add-to-cart' ? 50 : 100;
      await new Promise((resolve) => setTimeout(resolve, delay));
      const cartData = await this.#fetchCart();
      const cartItem = this.#findCartItem(cartData?.items || [], productId, variantId);

      if (cartData) {
        this.#previousCartState = cartData;
      }

      if (cartItem) {
        this.#addedProduct = this.#mapCartItemToAddedProduct(cartItem);
        // Override title with translated version from form if available
        if (productTitle) {
          this.#addedProduct.title = productTitle;
        }
      } else {
        this.#addedProduct = this.#mapEventDataToAddedProduct(eventData, productTitle);
      }
    } catch (error) {
      console.error('Cart modal failed to fetch cart state', error);
      this.#addedProduct = this.#mapEventDataToAddedProduct(eventData, productTitle);
    }

    this.#renderAddedProduct();
    this.#renderUpsellProducts(productId);
    this.#hideLoadingState();
  };

  #handleCartError = () => {
    this.#hideLoadingState();
    if (this.dialogEl?.open) {
      this.closeDialog();
    }
  };

  /**
   * @param {CartAddEvent} event
   */
  handleCartAddEvent(event) {
    return this.#handleCartAdd(event);
  }

  handleCartErrorEvent() {
    return this.#handleCartError();
  }

  #showLoadingState() {
    this.#isLoading = true;
    this.#updateHeaderText('loading');
    this.loadingStateEl?.removeAttribute('hidden');
    this.modalBodyEl?.setAttribute('hidden', 'true');
  }

  #hideLoadingState() {
    this.#isLoading = false;
    this.loadingStateEl?.setAttribute('hidden', 'true');
    this.modalBodyEl?.removeAttribute('hidden');
    this.#updateHeaderText('success');
  }

  #hideUpsellSection() {
    this.upsellSection?.setAttribute('hidden', 'true');
    if (this.upsellProductsContainer) {
      this.upsellProductsContainer.innerHTML = '';
    }
  }

  /**
   * @param {Record<string, unknown>} [sourceData]
   * @returns {Record<string, string>}
   */
  #getStrings(sourceData) {
    const win = /** @type {any} */ (window);
    const fromWindow = win?.themeStrings;
    if (fromWindow && typeof fromWindow === 'object') {
      return /** @type {Record<string, string>} */ (fromWindow);
    }
    if (sourceData && typeof sourceData === 'object') {
      return /** @type {Record<string, string>} */ (sourceData);
    }
    return {};
  }

  /**
   * @returns {Record<string, string>}
   */
  #getCartIcons() {
    const win = /** @type {any} */ (window);
    const icons = win?.cartIcons;
    if (icons && typeof icons === 'object') {
      return /** @type {Record<string, string>} */ (icons);
    }
    return {};
  }

  #renderAddedProduct() {
    if (!this.addedProductContainer) return;

    const product = this.#addedProduct;

    if (!product) {
      const fallbackMessage = this.#getTranslation(
        'content.product_added_generic',
        'Product successfully added to your cart.'
      );
      this.addedProductContainer.innerHTML = `
        <div class="cart-modal-added-product">
          <p class="cart-modal-added-product__message">${fallbackMessage}</p>
        </div>
      `;
      return;
    }

    const strings = this.#getStrings();
    const quantityLabel =
      strings.quantity_label || this.#getTranslation('content.quantity', 'Quantity');

    const price =
      typeof product.price === 'number'
        ? this.#formatMoney(product.price)
        : product.price || '';

    const imageMarkup = product.image
      ? `
        <div class="cart-modal-added-product__media">
          <img src="${product.image}" alt="${product.title}" loading="lazy" />
        </div>
      `
      : `
        <div class="cart-modal-added-product__media cart-modal-added-product__media--placeholder" aria-hidden="true">
          <span>✦</span>
        </div>
      `;

    this.addedProductContainer.innerHTML = `
      <div class="cart-modal-added-product">
        <div class="cart-modal-added-product__content">
          ${imageMarkup}
          <div class="cart-modal-added-product__details">
            <p class="cart-modal-added-product__title">${product.title}</p>
            ${product.variant ? `<p class="cart-modal-added-product__variant">${product.variant}</p>` : ''}
            ${price ? `<p class="cart-modal-added-product__price hidden">${price}</p>` : ''}
            <p class="cart-modal-added-product__quantity hidden">${quantityLabel}: ${product.quantity ?? 1}</p>
          </div>
        </div>
      </div>
    `;
  }

  #renderUpsellProducts(/** @type {string} */ productId) {
    if (!this.upsellProductsContainer) return;

    const allUpsellProducts = this.#extractUpsellProducts(document);
    const filteredProducts = this.#filterUpsellProductsForProduct(
      allUpsellProducts,
      productId
    );

    // Deduplicate products by product ID to prevent duplicate cards
    const seenProductIds = new Set();
    const upsellProducts = filteredProducts.filter((product) => {
      const productIdKey = product.id;
      if (!productIdKey || seenProductIds.has(productIdKey)) {
        return false;
      }
      seenProductIds.add(productIdKey);
      return true;
    });

    if (!upsellProducts.length) {
      this.#hideUpsellSection();
      return;
    }

    const markup = upsellProducts
      .map((product) => this.#renderUpsellCard(product))
      .join('');

    this.upsellProductsContainer.innerHTML = markup;
    this.#setupUpsellCardInteractions();
    this.#initializeQuantitySwitcher();
    this.#initializeUpsellTabs();
    this.#resetUpsellTabsToFirst();
    this.upsellSection?.removeAttribute('hidden');
    requestAnimationFrame(() => {
      this.#refreshTabIndicator();
      this.#refreshUpsellCarousels();
    });
  }

  #refreshUpsellCarousels() {
    if (typeof window.initCartUpsellCarousels === 'function') {
      window.initCartUpsellCarousels(this);
    }
  }

  #resetUpsellTabsToFirst() {
    const tabsRoot = this.querySelector('[data-cart-modal-tabs]');
    if (!(tabsRoot instanceof HTMLElement)) return;

    const tabs = Array.from(tabsRoot.querySelectorAll('.cart-modal__tab')).filter(
      (el) => el instanceof HTMLElement
    );
    const panels = Array.from(tabsRoot.querySelectorAll('[role="tabpanel"]')).filter(
      (el) => el instanceof HTMLElement
    );

    const firstTab = tabs[0];
    if (!firstTab || !panels.length) return;

    const controlsId = firstTab.getAttribute('aria-controls');
    if (!controlsId) return;

    for (const tab of tabs) {
      const isActive = tab === firstTab;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      tab.classList.toggle('cart-modal__tab--selected', isActive);
    }

    for (const panel of panels) {
      const isTarget = panel.id === controlsId;
      isTarget ? panel.removeAttribute('hidden') : panel.setAttribute('hidden', 'true');
      const panelContent = panel.querySelector('.cart-modal__tabpanel-content');
      if (panelContent instanceof HTMLElement) {
        panelContent.classList.remove('cart-modal__tabpanel-content--fade-out');
        panelContent.style.opacity = '';
      }
    }

    const nav = tabsRoot.querySelector('.cart-modal__tablist-wrapper');
    const indicator = tabsRoot.querySelector('.cart-modal__tab-indicator');
    if (nav instanceof HTMLElement && indicator instanceof HTMLElement) {
      this.#updateTabIndicator(nav, firstTab, indicator);
    }
  }

  /**
   * Positions the tab indicator under the given tab (plain JS animation).
   * @param {HTMLElement} nav
   * @param {HTMLElement} tab
   * @param {HTMLElement} indicator
   */
  #updateTabIndicator(nav, tab, indicator) {
    const navRect = nav.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const left = tabRect.left - navRect.left + nav.scrollLeft;
    const width = tabRect.width;
    indicator.style.left = `${left}px`;
    indicator.style.width = `${width}px`;
  }

  /** Re-positions the tab indicator to the currently selected tab (e.g. after upsell section becomes visible). */
  #refreshTabIndicator() {
    const tabsRoot = this.querySelector('[data-cart-modal-tabs]');
    const nav = tabsRoot?.querySelector('.cart-modal__tablist-wrapper');
    const indicator = tabsRoot?.querySelector('.cart-modal__tab-indicator');
    const selectedTab = tabsRoot?.querySelector('.cart-modal__tab[aria-selected="true"]');
    if (nav instanceof HTMLElement && indicator instanceof HTMLElement && selectedTab instanceof HTMLElement) {
      this.#updateTabIndicator(nav, selectedTab, indicator);
    }
  }

  #initializeUpsellTabs() {
    if (this.#tabsInitialized) return;

    const tabsRoot = this.querySelector('[data-cart-modal-tabs]');
    if (!(tabsRoot instanceof HTMLElement)) return;

    const tablist = tabsRoot.querySelector('[role="tablist"]');
    const nav = tabsRoot.querySelector('.cart-modal__tablist-wrapper');
    const indicator = tabsRoot.querySelector('.cart-modal__tab-indicator');
    if (!(tablist instanceof HTMLElement) || !(nav instanceof HTMLElement) || !(indicator instanceof HTMLElement)) return;

    /** @type {HTMLElement[]} */
    const tabs = Array.from(tabsRoot.querySelectorAll('.cart-modal__tab')).filter(
      (el) => el instanceof HTMLElement
    );

    /** @type {HTMLElement[]} */
    const panels = Array.from(tabsRoot.querySelectorAll('[role="tabpanel"]')).filter(
      (el) => el instanceof HTMLElement
    );

    if (!tabs.length || !panels.length) return;
    const firstTab = tabs[0];
    if (!firstTab) return;

    const FADE_DURATION_MS = 250;

    const selectTabImmediate = (/** @type {HTMLElement} */ tabEl) => {
      const controlsId = tabEl.getAttribute('aria-controls');
      if (!controlsId) return;

      for (const tab of tabs) {
        const isActive = tab === tabEl;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
        tab.classList.toggle('cart-modal__tab--selected', isActive);
      }

      for (const panel of panels) {
        const isTarget = panel.id === controlsId;
        isTarget ? panel.removeAttribute('hidden') : panel.setAttribute('hidden', 'true');
      }

      this.#updateTabIndicator(nav, tabEl, indicator);
    };

    const selectTab = (/** @type {HTMLElement} */ tabEl) => {
      if (tabEl.getAttribute('aria-selected') === 'true') return;

      const controlsId = tabEl.getAttribute('aria-controls');
      if (!controlsId) return;

      const currentTab = tabs.find((t) => t.getAttribute('aria-selected') === 'true');
      const currentPanel = currentTab ? panels.find((p) => p.id === currentTab.getAttribute('aria-controls')) : null;
      const nextPanel = panels.find((p) => p.id === controlsId);
      const currentContent = currentPanel?.querySelector('.cart-modal__tabpanel-content');
      const nextContent = nextPanel?.querySelector('.cart-modal__tabpanel-content');

      if (!nextPanel || !(nextContent instanceof HTMLElement)) {
        selectTabImmediate(tabEl);
        return;
      }

      const finishSwitch = () => {
        currentPanel?.setAttribute('hidden', 'true');
        nextPanel.removeAttribute('hidden');
        for (const tab of tabs) {
          const isActive = tab === tabEl;
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          tab.setAttribute('tabindex', isActive ? '0' : '-1');
          tab.classList.toggle('cart-modal__tab--selected', isActive);
        }
        this.#updateTabIndicator(nav, tabEl, indicator);
        this.#refreshUpsellCarousels();
      };

      const fadeInNewPanel = () => {
        nextContent.style.opacity = '0';
        nextContent.offsetHeight;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            nextContent.style.opacity = '1';
            this.#refreshUpsellCarousels();
          });
        });
      };

      if (currentContent instanceof HTMLElement && currentPanel && !currentPanel.hasAttribute('hidden')) {
        currentContent.classList.add('cart-modal__tabpanel-content--fade-out');
        currentContent.style.opacity = '0';

        const onFadeOutEnd = () => {
          currentContent.removeEventListener('transitionend', onFadeOutEnd);
          finishSwitch();
          fadeInNewPanel();
        };

        currentContent.addEventListener('transitionend', onFadeOutEnd, { once: true });
        setTimeout(() => {
          if (currentContent.classList.contains('cart-modal__tabpanel-content--fade-out')) {
            currentContent.removeEventListener('transitionend', onFadeOutEnd);
            finishSwitch();
            fadeInNewPanel();
          }
        }, FADE_DURATION_MS + 50);
      } else {
        finishSwitch();
        fadeInNewPanel();
      }
    };

    const initiallySelected =
      tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || firstTab;
    selectTabImmediate(initiallySelected);

    tablist.addEventListener('click', (event) => {
      const clicked = event.target instanceof Element ? event.target.closest('.cart-modal__tab') : null;
      if (!(clicked instanceof HTMLElement)) return;
      selectTab(clicked);
    });

    tablist.addEventListener('keydown', (event) => {
      const { key } = event;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) return;

      const activeIndex = Math.max(
        0,
        tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
      );

      let nextIndex = activeIndex;
      if (key === 'ArrowLeft') nextIndex = (activeIndex - 1 + tabs.length) % tabs.length;
      if (key === 'ArrowRight') nextIndex = (activeIndex + 1) % tabs.length;
      if (key === 'Home') nextIndex = 0;
      if (key === 'End') nextIndex = tabs.length - 1;

      event.preventDefault();
      const nextTab = tabs[nextIndex];
      if (!nextTab) return;
      selectTab(nextTab);
      try {
        nextTab.focus();
      } catch (err) {
        // non-critical
      }
    });

    this.#tabsInitialized = true;
  }

  #initializeQuantitySwitcher() {
    if (!this.upsellSection) return;

    const containers = this.upsellSection.querySelectorAll('[data-quantity-switcher] select[data-cart-modal-quantity]');
    for (const selectEl of containers) {
      if (!(selectEl instanceof HTMLSelectElement)) continue;
      if (this.#attachedQuantitySwitchers.has(selectEl)) continue;
      
      this.#attachedQuantitySwitchers.add(selectEl);
      
      selectEl.addEventListener('change', (e) => {
        const target = e.target;
        if (!target || !(target instanceof HTMLSelectElement)) return;
        if (target.value !== CartModalComponent.QUANTITY_SWITCHER_TRIGGER) return;

        const wrapper = target.closest('[data-quantity-switcher]');
        if (!wrapper) return;

        const input = document.createElement('input');
        input.type = 'number';
        input.name = 'quantity';
        input.value = CartModalComponent.QUANTITY_SWITCHER_TRIGGER;
        input.className = 'cart-modal-upsell-card__quantity input__field !nf-border-none !nf-focus-outline-none !nf-focus-visible:outline-none nf-w-[70px] nf-pr-[20px] nf-bg-transparent';
        input.setAttribute('data-cart-modal-quantity', 'true');
        input.setAttribute('aria-label', this.#getTranslation('accessibility.quantity', 'Quantity'));
        input.min = String(CartModalComponent.MIN_QUANTITY);

        wrapper.innerHTML = '';
        wrapper.appendChild(input);

        // Sync with hidden form input
        const card = wrapper.closest('.cart-modal-upsell-card');
        if (card) {
          const form = card.querySelector('form');
          const quantityInput = form?.querySelector('input[name="quantity"]');
          if (quantityInput instanceof HTMLInputElement) {
            quantityInput.value = input.value;
          }
        }

        // Listen for changes on the number input
        input.addEventListener('change', () => {
          const card = input.closest('.cart-modal-upsell-card');
          if (card) {
            const form = card.querySelector('form');
            const quantityInput = form?.querySelector('input[name="quantity"]');
            if (quantityInput instanceof HTMLInputElement) {
              quantityInput.value = input.value;
            }
          }
        });

        try {
          input.focus();
        } catch (err) {
          // Focus errors can occur when element is not focusable or document is not active
          // This is non-critical and can be safely ignored
        }
      });
    }
  }

  #setupUpsellCardInteractions() {
    if (!this.upsellSection) return;

    // Sync quantity selector/input with form input (all tab panels, including collection tabs)
    const quantitySelects = this.upsellSection.querySelectorAll('select[data-cart-modal-quantity]');
    const quantityInputs = this.upsellSection.querySelectorAll('input[type="number"][data-cart-modal-quantity]');

    quantitySelects.forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;
      
      // Initialize the hidden input with the select value
      const card = select.closest('.cart-modal-upsell-card');
      if (card) {
        const form = card.querySelector('form');
        const quantityInput = form?.querySelector('input[name="quantity"]');
        if (quantityInput instanceof HTMLInputElement) {
          quantityInput.value = select.value;
        }
      }
      
      // Listen for changes
      select.addEventListener('change', (e) => {
        const target = e.target;
        if (!(target instanceof HTMLSelectElement)) return;
        
        const card = select.closest('.cart-modal-upsell-card');
        if (!card) return;
        
        const form = card.querySelector('form');
        const quantityInput = form?.querySelector('input[name="quantity"]');
        if (quantityInput instanceof HTMLInputElement) {
          quantityInput.value = target.value;
        }
      });
    });

    quantityInputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      
      // Initialize the hidden input with the number input value
      const card = input.closest('.cart-modal-upsell-card');
      if (card) {
        const form = card.querySelector('form');
        const quantityInput = form?.querySelector('input[name="quantity"]');
        if (quantityInput instanceof HTMLInputElement) {
          quantityInput.value = input.value;
        }
      }
      
      // Listen for changes
      input.addEventListener('change', (e) => {
        const target = e.target;
        if (!(target instanceof HTMLInputElement)) return;
        
        const card = input.closest('.cart-modal-upsell-card');
        if (!card) return;
        
        const form = card.querySelector('form');
        const quantityInput = form?.querySelector('input[name="quantity"]');
        if (quantityInput instanceof HTMLInputElement) {
          quantityInput.value = target.value;
        }
      });
    });

    // Handle upsell add-to-cart button clicks and form submissions (all tab panels)
    const addToCartButtons = this.upsellSection.querySelectorAll('.cart-modal-upsell-card__button');
    const upsellForms = this.upsellSection.querySelectorAll('.cart-modal-upsell-card__form');
    
    // Prevent form submission (e.g., pressing Enter)
    upsellForms.forEach((form) => {
      if (!(form instanceof HTMLFormElement)) return;
      
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
    });

    addToCartButtons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      button.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const form = button.closest('form');
        if (!form) return;

        const card = button.closest('.cart-modal-upsell-card');
        if (!card) return;

        // Get variant ID and quantity from form inputs
        const variantIdInput = form.querySelector('input[name="id"]');
        const quantityInput = form.querySelector('input[name="quantity"]');
        const quantitySelect = card.querySelector('select[data-cart-modal-quantity]');
        const quantityNumberInput = card.querySelector('input[type="number"][data-cart-modal-quantity]');

        if (!variantIdInput || !(variantIdInput instanceof HTMLInputElement)) return;

        const variantId = parseInt(variantIdInput.value, 10);
        let quantity = 1;

        if (quantitySelect && quantitySelect instanceof HTMLSelectElement) {
          quantity = parseInt(quantitySelect.value, 10) || 1;
        } else if (quantityNumberInput && quantityNumberInput instanceof HTMLInputElement) {
          quantity = parseInt(quantityNumberInput.value, 10) || 1;
        } else if (quantityInput && quantityInput instanceof HTMLInputElement) {
          quantity = parseInt(quantityInput.value, 10) || 1;
        }

        if (!variantId || Number.isNaN(variantId) || quantity <= 0) return;

        // Clear any existing error messages
        if (card instanceof HTMLElement) {
          this.#hideUpsellError(card);
        }

        // Set loading state
        button.setAttribute('data-atc-state', 'loading');
        button.disabled = true;

        try {
          // Use cart/add.js API endpoint
          const cartAddUrl = '/cart/add.js';

          const response = await fetch(cartAddUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [{
                id: variantId,
                quantity: quantity,
                properties: { _added_from: 'PopUpsell' },
              }],
            }),
          });

          const result = await response.json();

          // Check if response has an error status (like 422)
          if (!response.ok || (result.status && result.status >= 400)) {
            const errorMessage = result.message || result.description || 'Failed to add item to cart';
            if (card instanceof HTMLElement) {
              this.#showUpsellError(card, errorMessage);
            }
            throw new Error(errorMessage);
          }

          // Remove loading state and show success state
          button.removeAttribute('data-atc-state');
          if (!button.classList.contains('atc-added')) {
            button.classList.add('atc-added');
          }

          // Remove success state after animation duration
          setTimeout(() => {
            button.classList.remove('atc-added');
          }, ADD_TO_CART_TEXT_ANIMATION_DURATION);

          // Re-enable button
          button.disabled = false;
        } catch (error) {
          console.error('Error adding upsell product to cart:', error);
          // Remove loading state on error
          button.removeAttribute('data-atc-state');
          // Re-enable button on error
          button.disabled = false;
          // Error message is already displayed by #showUpsellError if it was a 422 response
          // For other errors, show a generic message
          if (card instanceof HTMLElement && !card.querySelector('.cart-modal-upsell-card__error')) {
            this.#showUpsellError(card, error.message || 'Failed to add item to cart');
          }
        }
      });
    });
  }

  /**
   * Shows an error message above the pricing actions in an upsell card
   * @param {HTMLElement} card - The upsell card element
   * @param {string} message - The error message to display
   */
  #showUpsellError(/** @type {HTMLElement} */ card, /** @type {string} */ message) {
    if (!card) return;

    let errorEl = card.querySelector('.cart-modal-upsell-card__error');
    if (!errorEl) {
      // Create error element if it doesn't exist
      const pricingActions = card.querySelector('.cart-modal-upsell-card__pricing-actions');
      if (!pricingActions) return;

      errorEl = document.createElement('div');
      errorEl.className = 'cart-modal-upsell-card__error';
      errorEl.setAttribute('role', 'alert');
      errorEl.setAttribute('aria-live', 'assertive');
      pricingActions.parentNode?.insertBefore(errorEl, pricingActions);
    }

    if (errorEl instanceof HTMLElement) {
      errorEl.textContent = message;
      errorEl.style.display = 'block';

      // Auto-hide error after 10 seconds
      setTimeout(() => {
        this.#hideUpsellError(card);
      }, 10000);
    }
  }

  /**
   * Hides the error message in an upsell card
   * @param {HTMLElement} card - The upsell card element
   */
  #hideUpsellError(/** @type {HTMLElement} */ card) {
    if (!card) return;
    const errorEl = card.querySelector('.cart-modal-upsell-card__error');
    if (errorEl instanceof HTMLElement) {
      errorEl.style.display = 'none';
      errorEl.textContent = '';
    }
  }

  #renderUpsellCard(/** @type {UpsellProduct} */ product) {
    const strings = this.#getStrings();
    const addToCartText = strings.addToCart || 'Add to cart';
    const cartIcons = this.#getCartIcons();

    const bioIcon = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" class="icon icon-bio" width="24" height="24" viewBox="0 0 208 175" fill="currentColor" style="color:#41cb42" aria-hidden="true" focusable="false"><use href="#icon-bio" xlink:href="#icon-bio"></use></svg>`;

    const boxIcon = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" class="icon icon-box" width="11" height="12" viewBox="0 0 11.1605839 12.2016807" fill="currentColor" style="color:#80848c" aria-hidden="true" focusable="false"><use href="#icon-box" xlink:href="#icon-box"></use></svg>`;

    // Get translations
    const plasticFreeText = this.#getTranslation('products.product.characteristics_plasticvrij', 'Plastic-free');
    
    // Determine locale for pieces text formatting
    const localeSource = window.Shopify?.locale || document.documentElement?.lang || 'en';
    const locale = typeof localeSource === 'string' ? localeSource.split('-')[0] : 'en';
    const isEnglish = locale === 'en';
    
    // For English, use "Contains X pieces" format, for others use "X pieces" format
    const containsPiecesText = product.piecesPerBox 
      ? isEnglish
        ? this.#getTranslation('products.product.inhoud_stuks', 'Contains {{ stuks }} pieces').replace('{{ stuks }}', product.piecesPerBox)
        : `${product.piecesPerBox} ${this.#getTranslation('products.product.characteristics_pieces', 'pieces')}`
      : '';

    // Build quantity options
    const quantityOptions = Array.from({ length: 10 }, (_, i) => i + 1)
      .map(qty => `<option value="${qty}"${qty === 1 ? ' selected' : ''}>${qty}</option>`)
      .join('') + `<option value="${CartModalComponent.QUANTITY_SWITCHER_TRIGGER}">Meer</option>`;

          const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const vatMultiplier = hostname.endsWith('.de') ? 1.19 : 1.21;

const includeTaxesLabel =
  this.getAttribute('data-include-taxes-label') ||
  this.#getTranslation('products.product.include_taxes', 'incl. BTW');
      const priceInclVat = this.#calculateVatIncludedPrice(String(product.price || ''), vatMultiplier);


    return `
      <div class="cart-modal-upsell-card" data-product-id="${product.id}">
        <div class="cart-modal-upsell-card__media">
          <img
            src="${product.image}"
            alt="${product.title}"
            class="cart-modal-upsell-card__image"
            loading="lazy"
          />
          ${product.isBio ? `
            <div class="cart-modal-upsell-card__bio-icon">
              ${bioIcon}
            </div>
          ` : ''}
        </div>
        <div class="cart-modal-upsell-card__content">
          ${product.plasticFree ? `
            <span class="cart-modal-upsell-card__plastic-free">
              ${plasticFreeText}
            </span>
          ` : ''}
          <a href="${product.url}" class="cart-modal-upsell-card__title">
  ${product.title}
</a>          ${product.piecesPerBox ? `
            <div class="cart-modal-upsell-card__pieces">
              <span class="cart-modal-upsell-card__pieces-icon">${boxIcon}</span>
              <span class="cart-modal-upsell-card__pieces-text">${containsPiecesText}</span>
            </div>
          ` : ''}
          <div class="cart-modal-upsell-card__error" role="alert" aria-live="assertive" style="display: none;"></div>
          <div class="cart-modal-upsell-card__pricing-actions">
            <div class="cart-modal-upsell-card__pricing">
              ${product.compareAtPrice ? `
                <p class="cart-modal-upsell-card__price-compare">${product.compareAtPrice}</p>
              ` : ''}
              <p class="cart-modal-upsell-card__price">${product.price}</p>
            </div>
            <div class="cart-modal-upsell-card__actions">
            <div class="cart-modal-upsell-card__quantity-wrapper" data-quantity-switcher>
              <select 
                class="cart-modal-upsell-card__quantity"
                name="quantity"
                data-cart-modal-quantity="true"
                aria-label="${this.#getTranslation('accessibility.quantity', 'Quantity')}"
              >
                ${quantityOptions}
              </select>
            </div>
            <product-form-component
              data-section-id="cart-modal-upsell-${product.id}"
              data-product-id="${product.id}"
              data-product-url="${product.url}"
              data-quantity-default="1"
            >
              <div
                class="visually-hidden"
                aria-live="assertive"
                role="status"
                aria-atomic="true"
                ref="liveRegion"
              ></div>
              <form
                action="/cart/add"
                method="post"
                enctype="multipart/form-data"
                data-type="add-to-cart-form"
                class="cart-modal-upsell-card__form"
              >
                <input type="hidden" name="id" value="${product.variantId}" ref="variantId">
                <input type="hidden" name="quantity" value="1" ref="quantityInput">
                <add-to-cart-component>
                  <button
                    type="submit"
                    name="add"
                    ref="addToCartButton"
                    class="cart-modal-upsell-card__button"
                  >
                    <span class="add-to-cart-text">
                      <span class="svg-wrapper add-to-cart-icon">
                        ${cartIcons.addToCart || ''}
                      </span>
                    </span>
                    <span aria-hidden="true" class="add-to-cart-text--added">
                      <span class="svg-wrapper add-to-cart-icon--added">
                        <span class="svg-wrapper add-to-cart-icon-is-loading">
                          <span class="nf-loading-circle" style="--nf-spinner-size: 20px; --nf-spinner-color: rgba(255, 255, 255, 0.9); --nf-spinner-track-color: rgba(255, 255, 255, 0.25);"></span>
                        </span>
                        <span class="svg-wrapper add-to-cart-icon-is-added">
                          ${cartIcons.added || ''}
                        </span>
                      </span>
                    </span>
                  </button>
                </add-to-cart-component>
              </form>
            </product-form-component>
            </div>
          </div>
                    ${priceInclVat ? `
            <span class="inclvat nf-include-vat-line">
              ${priceInclVat}&nbsp;${includeTaxesLabel}
            </span>
          ` : ''}

        </div>
      </div>
    `;
  }

  #extractUpsellProducts(/** @type {Document} */ doc) {
    /** @type {UpsellProduct[]} */
    const upsellProducts = [];
    const elements = doc.querySelectorAll('[data-upsell-product]');

    elements.forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      const parentProductId = element.getAttribute('data-parent-product-id');
      const productId = element.getAttribute('data-upsell-product-id');
      const variantId = element.getAttribute('data-upsell-product-variant-id');
      const title = element.getAttribute('data-upsell-product-title');
      const price = element.getAttribute('data-upsell-product-price');
      const compareAtPrice = element.getAttribute('data-upsell-product-compare-at-price');
      const image = element.getAttribute('data-upsell-product-image');
      const url = element.getAttribute('data-upsell-product-url');
      const piecesPerBox = element.getAttribute('data-upsell-product-pieces-per-box');
      const isBio = element.getAttribute('data-upsell-product-is-bio') === 'true';
      const plasticFree = element.getAttribute('data-upsell-product-plastic-free') === 'true';

      if (parentProductId && productId && variantId && title) {
        upsellProducts.push({
          parentProductId,
          id: productId,
          variantId,
          title,
          price: price || '',
          compareAtPrice: compareAtPrice || '',
          image: image || '',
          url: url || `/products/${productId}`,
          piecesPerBox: piecesPerBox || '',
          isBio,
          plasticFree,
        });
      }
    });

    return upsellProducts;
  }

  #filterUpsellProductsForProduct(
    /** @type {UpsellProduct[]} */ allUpsellProducts,
    /** @type {string} */ addedProductId
  ) {
    if (!addedProductId) return [];
    const normalizedId = String(addedProductId);

    return allUpsellProducts.filter((product) => {
      if (!product.parentProductId) return false;
      return (
        String(product.parentProductId) === normalizedId ||
        product.parentProductId === addedProductId
      );
    });
  }

  #findCartItem(
    /** @type {CartItem[]} */ items,
    /** @type {string} */ productId,
    /** @type {string | null} */ variantId
  ) {
    if (!items || !items.length) return null;

    const normalizedVariantId = variantId ? String(variantId) : null;
    const normalizedProductId = productId ? String(productId) : null;

    if (normalizedVariantId) {
      const variantMatch = items.find((item) => {
        return (
          String(item.variant_id) === normalizedVariantId ||
          String(item.id) === normalizedVariantId
        );
      });
      if (variantMatch) return variantMatch;
    }

    if (normalizedProductId) {
      const productMatch = items.find((item) => {
        return (
          String(item.product_id) === normalizedProductId ||
          item.handle === normalizedProductId
        );
      });
      if (productMatch) return productMatch;
    }

    if (this.#previousCartState?.items?.length) {
      const newItems = items.filter((currentItem) => {
        return !this.#previousCartState?.items?.some(
          (previousItem) => previousItem.variant_id === currentItem.variant_id
        );
      });
      if (newItems.length) return newItems[0];
    }

    return items[items.length - 1] || null;
  }

  async #fetchCart() {
    const response = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to load cart state');
    }

    return /** @type {CartState} */ (await response.json());
  }

  #mapCartItemToAddedProduct(/** @type {CartItem} */ cartItem) {
    return {
      productId: String(cartItem.product_id),
      variantId: String(cartItem.variant_id),
      quantity: cartItem.quantity,
      title: cartItem.product_title,
      price: cartItem.final_price,
      image: cartItem.image,
      variant: cartItem.variant_title,
    };
  }

  /**
   * Extracts product title from form input (for translated product titles)
   * @param {string} productId - The product ID to find the form for
   * @returns {string | null} The product title from the form input, or null if not found
   */
  #getProductTitleFromForm(/** @type {string} */ productId) {
    if (!productId) return null;

    // Find product-form-component matching the productId
    const productFormComponents = document.querySelectorAll('product-form-component');
    for (const component of productFormComponents) {
      if (component instanceof HTMLElement && component.dataset.productId === String(productId)) {
        const form = component.querySelector('form');
        if (form) {
          const productTitleInput = form.querySelector('input[name="product-title"]');
          if (productTitleInput instanceof HTMLInputElement && productTitleInput.value) {
            return productTitleInput.value;
          }
        }
      }
    }

    return null;
  }

  #mapEventDataToAddedProduct(/** @type {CartEventData} */ data, /** @type {string | null} */ productTitle = null) {
    if (!data) return null;

    // Prioritize productTitle from form input (for translated product titles)
    const title = productTitle || data.title || (data.addedItem?.title);
    const price = typeof data.price === 'number' ? data.price : data.finalPrice;
    const image = data.image || data.featuredImage;
    const productIdRaw =
      data.productId !== undefined && data.productId !== null
        ? data.productId
        : data.id !== undefined && data.id !== null
          ? data.id
          : null;
    const variantIdRaw =
      data.variantId !== undefined && data.variantId !== null ? data.variantId : null;

    if (!title && !price && !image) return null;

    return {
      productId: productIdRaw === null ? null : String(productIdRaw),
      variantId: variantIdRaw === null ? null : String(variantIdRaw),
      quantity: data.itemCount || data.quantity || 1,
      title: title || '',
      price: typeof price === 'number' ? price : 0,
      image: image || '',
      variant: data.variant || data.variantTitle || '',
    };
  }

  #extractProductIdentifier(/** @type {CartEventData} */ data) {
    if (!data) return null;
    if (data.productId) return String(data.productId);
    if (data.id) return String(data.id);
    if (data.handle) return data.handle;
    if (data.productHandle) return data.productHandle;
    return null;
  }

  #announce(/** @type {string} */ message) {
    if (!this.liveRegion) return;
    this.liveRegion.textContent = '';
    requestAnimationFrame(() => {
      if (this.liveRegion) {
        this.liveRegion.textContent = message;
      }
    });
  }

  #updateHeaderText(/** @type {'loading' | 'success'} */ state) {
    const headerIcon = this.querySelector('.cart-modal__header-icon');
    const headerText = this.querySelector('.cart-modal__header-text');

    if (!(headerIcon instanceof HTMLElement) || !(headerText instanceof HTMLElement)) {
      return;
    }

    if (state === 'loading') {
      headerIcon.style.display = 'none';
      headerText.textContent = this.#getTranslation('content.loading_cart', 'Loading cart...');
    } else {
      headerIcon.style.display = 'inline';
      headerText.textContent = this.#getTranslation(
        'content.product_added_to_cart',
        'The product is added to cart'
      );
    }
  }

  #getTranslation(/** @type {string} */ key, /** @type {string} */ fallback) {
    const strings = this.#getStrings();
    const value = strings[key];
    return typeof value === 'string' ? value : fallback;
  }

    // Formats cents to a localized number string WITHOUT currency symbol
  #formatMoneyWithoutCurrency(/** @type {number} */ amount) {
    if (typeof amount !== 'number' || Number.isNaN(amount)) return '';

    const localeSource = window.Shopify?.locale || document.documentElement?.lang || 'en';
    const locale =
      typeof localeSource === 'string' ? localeSource.replace('_', '-') : 'en';

    try {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount / 100);
    } catch (error) {
      console.error('Failed to format money without currency', error);
      return (amount / 100).toFixed(2);
    }
  }


  #formatMoney(/** @type {number} */ amount) {
    if (typeof amount !== 'number' || Number.isNaN(amount)) return '';

    const localeSource = window.Shopify?.locale || document.documentElement?.lang || 'en';
    const locale =
      typeof localeSource === 'string' ? localeSource.replace('_', '-') : 'en';
    const shopifyCurrency = window.Shopify?.currency;
    const currency =
      typeof shopifyCurrency === 'object' && shopifyCurrency !== null && 'active' in shopifyCurrency
        ? shopifyCurrency.active
        : typeof shopifyCurrency === 'string'
          ? shopifyCurrency
          : 'USD';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      }).format(amount / 100);
    } catch (error) {
      console.error('Failed to format money in cart modal', error);
      return (amount / 100).toFixed(2);
    }
  }

  /**
   * Calculates VAT-included price matching nf-include-vat.liquid logic
   * Formula: price * vatMultiplier (where vatMultiplier is 1.19 or 1.21)
   * @param {string} priceString - Price string excl. VAT (e.g., "19.75" or "19,75")
   * @param {number} vatMultiplier - VAT multiplier (1.19 for 19% or 1.21 for 21%)
   * @returns {string} Formatted price string
   */
  #calculateVatIncludedPrice(/** @type {string} */ priceString, /** @type {number} */ vatMultiplier) {
    if (typeof priceString !== 'string' || !priceString.trim()) return priceString;
    if (typeof vatMultiplier !== 'number' || Number.isNaN(vatMultiplier)) return priceString;

    try {
      // Parse price: remove currency symbols, replace comma with dot (matching Liquid: money_without_currency | replace: ',', '.')
      const numericPrice = parseFloat(
        priceString.replace(/[^\d,.-]/g, '').replace(',', '.')
      );
      if (Number.isNaN(numericPrice)) return priceString;

      // Calculate VAT-included price: multiply by vatMultiplier (matching Liquid: times: vatcost)
      // Round to 2 decimals (matching Liquid: round: 2)
      const vatIncludedPrice = Math.round((numericPrice * vatMultiplier) * 100) / 100;

      // Format as money (convert to cents, then format)
      return this.#formatMoneyWithoutCurrency(Math.round(vatIncludedPrice * 100));
    } catch (error) {
      console.error('Error calculating VAT-included price for cart modal', error);
      return priceString;
    }
  }

  #calculateVatPrice(/** @type {string} */ priceString) {
    if (typeof priceString !== 'string' || !priceString.trim()) return '';

    try {
      const numericPrice = parseFloat(
        priceString.replace(/[^\d,.-]/g, '').replace(',', '.')
      );
      if (Number.isNaN(numericPrice)) return priceString;

      const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
      let vatRate = 0.21;
      let vatLabel = 'incl. BTW';

      if (hostname.endsWith('.de')) {
        vatRate = 0.19;
        vatLabel = 'inkl. MwSt.';
      }

      const vatPrice = numericPrice * (1 + vatRate);
      const formattedPrice = this.#formatMoney(Math.round(vatPrice * 100));

      return `${formattedPrice} ${vatLabel}`;
    } catch (error) {
      console.error('Error calculating VAT price for cart modal', error);
      return priceString;
    }
  }

  open() {
    if (!this.dialogEl?.open) {
      this.showDialog();
    }
    this.#resetUpsellTabsToFirst();
    requestAnimationFrame(() => this.#refreshTabIndicator());
    if (!this.#isLoading) {
      this.#hideLoadingState();
    }
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-modal-component')) {
  customElements.define('cart-modal-component', CartModalComponent);
}

const cartModalLazyContainerSelector = '[id^="CartModalContainer-"][data-section-id]';

const isCartModalLazyPending = () =>
  document.querySelector(cartModalLazyContainerSelector) !== null &&
  document.querySelector('cart-modal-component') === null;

let cartModalSectionLoadPromise = /** @type {Promise<void> | null} */ (null);

async function ensureCartModalLoaded() {
  if (!isCartModalLazyPending()) return;

  const container = document.querySelector(cartModalLazyContainerSelector);
  const sectionId = container?.dataset?.sectionId;
  if (!container || !sectionId) return;

  if (!cartModalSectionLoadPromise) {
    cartModalSectionLoadPromise = (async () => {
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('sections', sectionId);
      const response = await fetch(url.toString());

      if (!response.ok) return;

      const data = await response.json();
      const html = data[sectionId];
      if (!html || html === 'null') return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const sectionRoot = parsed.querySelector('.shopify-section');
      const component = sectionRoot?.querySelector('cart-modal-component');

      if (!(component instanceof HTMLElement) || !container.parentNode) return;

      container.replaceWith(component);
    })().finally(() => {
      cartModalSectionLoadPromise = null;
    });
  }

  await cartModalSectionLoadPromise;
}

document.addEventListener(
  CartAddEvent.eventName,
  async (event) => {
    if (!isCartModalLazyPending()) return;
    await ensureCartModalLoaded();
    const modal = document.querySelector('cart-modal-component');
    await modal?.handleCartAddEvent?.(/** @type {CartAddEvent} */ (event));
  },
  true
);

document.addEventListener(
  ThemeEvents.cartError,
  async () => {
    if (!isCartModalLazyPending()) return;
    await ensureCartModalLoaded();
    document.querySelector('cart-modal-component')?.handleCartErrorEvent?.();
  },
  true
);

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-cart-modal-open]');
  if (!trigger) return;

  event.preventDefault();

  void (async () => {
    await ensureCartModalLoaded();
    document.querySelector('cart-modal-component')?.open();
  })();
});

/**
 * @typedef {Object} CartState
 * @property {Array<CartItem>} items
 */

/**
 * @typedef {Object} CartItem
 * @property {number} product_id
 * @property {number} variant_id
 * @property {number} id
 * @property {string} handle
 * @property {string} product_title
 * @property {string} variant_title
 * @property {number} quantity
 * @property {number} final_price
 * @property {string} image
 */

/**
 * @typedef {Object} AddedProduct
 * @property {string | null} productId
 * @property {string | null} variantId
 * @property {number} quantity
 * @property {string} title
 * @property {number | string} price
 * @property {string} image
 * @property {string} variant
 */

/**
 * @typedef {Object} UpsellProduct
 * @property {string} parentProductId
 * @property {string} id
 * @property {string} variantId
 * @property {string} title
 * @property {string} price
 * @property {string} compareAtPrice
 * @property {string} image
 * @property {string} url
 * @property {string} piecesPerBox
 * @property {boolean} isBio
 * @property {boolean} plasticFree
 */

/**
 * @typedef {Object} CartEventData
 * @property {string | number} [productId]
 * @property {string | number} [id]
 * @property {string} [handle]
 * @property {string} [productHandle]
 * @property {string | number} [variantId]
 * @property {string} [title]
 * @property {number} [price]
 * @property {number} [finalPrice]
 * @property {string} [image]
 * @property {string} [featuredImage]
 * @property {number} [itemCount]
 * @property {number} [quantity]
 * @property {string} [variant]
 * @property {string} [variantTitle]
 * @property {boolean} [didError]
 * @property {string} [source]
 * @property {{title?: string}} [addedItem]
 */
